import {
  countVouchLetters,
  INVALID_CHARS,
  MIN_VOUCH_LETTERS,
  NAME_PATTERN,
  PHONE_REGEX,
  PHONE_SEPARATOR_PATTERN,
  PHONE_WHITESPACE_PATTERN,
  REPEATED_CHAR_PATTERN,
} from "@/lib/constants";
import {
  type AttributePill,
  type ContactFormState,
  type Meta,
  type ProfileViewModel,
  type VoucherDetailsErrors,
  type VoucherDetailsFields,
} from "@/lib/types";
import { clsx, type ClassValue } from "clsx";
import {
  Baby,
  BriefcaseBusiness,
  Calendar,
  Globe,
  GraduationCap,
  Heart,
  MoonStar,
  Ruler,
} from "lucide-react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getInitials(name: string): string {
  if (!name) return "";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function formatChildren(count: number): string {
  if (count === 0) return "No children";
  if (count === 1) return "1 child";
  return `${count} children`;
}

export function getAttributePills(profile: ProfileViewModel): AttributePill[] {
  const pills: AttributePill[] = [];

 
  if (profile.Age) {
    pills.push({ icon: Calendar, label: `${profile.Age} years` });
  }
  if (profile.Ethnicity) {
    pills.push({ icon: Globe, label: profile.Ethnicity });
  }
  if (profile.Height) {
    pills.push({ icon: Ruler, label: profile.Height });
  }
  if (profile.MaritalStatus) {
    pills.push({ icon: Heart, label: profile.MaritalStatus });
  }
  if (profile.Children) {
    pills.push({ icon: Baby, label: profile.Children });
  }
  if (profile.Education) {
    pills.push({ icon: GraduationCap, label: profile.Education });
  }
  if (profile.Occupation) {
    pills.push({ icon: BriefcaseBusiness, label: profile.Occupation });
  }
  if (profile.Sect) {
    pills.push({ icon: MoonStar, label: profile.Sect });
  }

  return pills;
}

export const isFieldValid = (field: keyof ContactFormState, value: string) => {
  if (!value.trim()) return false;
  if (field === "phone") return PHONE_REGEX.test(value);
  return true;
};

const VOUCHER_PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

const VOUCHER_DETAILS_RULES: Record<
  keyof VoucherDetailsFields,
  { isValid: (value: string) => boolean; message: string }[]
> = {
  Name: [
    {
      isValid: (value) => value.length >= 3,
      message: "Name must be at least 3 characters long.",
    },
    {
      isValid: (value) => value.length <= 30,
      message: "Name cannot be longer than 30 chars.",
    },
    {
      isValid: (value) => !INVALID_CHARS.test(value) && NAME_PATTERN.test(value),
      message: "Name cannot contain special characters.",
    },
    {
      isValid: (value) => !REPEATED_CHAR_PATTERN.test(value),
      message: "Please enter a valid name.",
    },
  ],
  Phone: [
    {
      isValid: (value) => !PHONE_WHITESPACE_PATTERN.test(value),
      message: "Phone number cannot contain spaces.",
    },
    {
      isValid: (value) => !PHONE_SEPARATOR_PATTERN.test(value),
      message: "Phone number cannot contain special characters.",
    },
    {
      isValid: (value) => VOUCHER_PHONE_REGEX.test(value),
      message:
        "Phone number must start with '+' and country calling code (e.g. +1, +44).",
    },
  ],
  Comments: [
    {
      isValid: (value) => countVouchLetters(value) >= MIN_VOUCH_LETTERS,
      message: "Your response is too short.",
    },
  ],
};

export const validateMeta = (
  fields: VoucherDetailsFields,
): VoucherDetailsErrors => {
  const errors: VoucherDetailsErrors = {};
  (
    Object.keys(VOUCHER_DETAILS_RULES) as Array<keyof VoucherDetailsFields>
  ).forEach((field) => {
    const value = fields[field]?.trim();
    if (!value) return;
    const failed = VOUCHER_DETAILS_RULES[field].find(
      (rule) => !rule.isValid(value),
    );
    if (failed) errors[field] = failed.message;
  });
  return errors;
};

export const maskPhone = (phone: string): string =>
  phone.slice(0, 3) + phone.slice(3, -3).replace(/\d/g, "•") + phone.slice(-3);

export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Strips `Comments` from meta only when there's no negative answer — i.e. both
 * `Accuracy` and `Endorsement` are `true` and `Comments` exists but is an empty
 * string.
 */
export function sanitizeMetadata(meta: Partial<Meta>): Partial<Meta> {
  const result = { ...meta };
  if (
    result.Accuracy &&
    result.Endorsement &&
    "Comments" in result &&
    result.Comments?.trim() === ""
  ) {
    delete result.Comments;
  }
  return result;
}
